import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { schedulingLinksApi, SchedulingLink, SchedulingLinkFormData, HUBSPOT_LEAD_STATUS_OPTIONS, HubSpotLeadStatus } from '../services/api';
import { detectTimezone, TIMEZONES } from '../utils/timezones';
import { useAuth } from '../context/AuthContext';
import ConditionalReminderSettings from '../components/ConditionalReminderSettings';
import QRCodeSVG from 'react-qr-code';
import {
    Link as LinkIcon,
    Plus,
    Copy,
    Check,
    Clock,
    Calendar,
    Settings,
    ToggleLeft,
    ToggleRight,
    Trash2,
    Edit,
    Users,
    ExternalLink,
    X,
    Ban,
    CalendarX,
    Zap,
    ClipboardList,
    Sun,
    Moon,
    Monitor,
    Upload,
    Image as ImageIcon,
    Bell,
    Eye,
    Globe,
    FileText,
    Mail,
    Phone,
    MessageSquare,
    List,
    CheckSquare,
    ChevronLeft,
    QrCode,
    Download
} from 'lucide-react';

// Local "today" as YYYY-MM-DD. Uses local date components, not
// toISOString() (which is UTC): for an owner west of UTC, UTC has already
// rolled to tomorrow in the evening, which would set the date-input min to
// tomorrow and prevent blocking today.
function localTodayStr(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

const DAYS_OF_WEEK = [
    { value: null, label: 'Every day' },
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
];

const TIMES = Array.from({ length: 24 }, (_, h) => {
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const period = h < 12 ? 'AM' : 'PM';
    return { value: `${h.toString().padStart(2, '0')}:00:00`, label: `${hour12}:00 ${period}` };
});

const DURATIONS = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' },
];

const REMINDER_OPTIONS = [
    { value: 15, label: '15 minutes before' },
    { value: 30, label: '30 minutes before' },
    { value: 60, label: '1 hour before' },
    { value: 120, label: '2 hours before' },
    { value: 1440, label: '1 day before' },
    { value: 2880, label: '2 days before' },
];

const FIELD_TYPES = [
    { value: 'text', label: 'Text', icon: FileText },
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'phone', label: 'Phone', icon: Phone },
    { value: 'textarea', label: 'Long Text', icon: MessageSquare },
    { value: 'select', label: 'Dropdown', icon: List },
    { value: 'checkbox', label: 'Checkboxes', icon: CheckSquare },
];

export function SchedulingLinksPage() {
    const [links, setLinks] = useState<SchedulingLink[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingLink, setEditingLink] = useState<SchedulingLink | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [qrCodeLink, setQrCodeLink] = useState<SchedulingLink | null>(null);

    useEffect(() => {
        loadLinks();
    }, []);

    const loadLinks = async () => {
        try {
            const data = await schedulingLinksApi.list();
            setLinks(data);
        } catch (err) {
            console.error('Failed to load scheduling links:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyLink = async (link: SchedulingLink) => {
        try {
            await navigator.clipboard.writeText(link.booking_url);
            setCopiedId(link.id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleToggle = async (link: SchedulingLink) => {
        try {
            await schedulingLinksApi.toggle(link.id);
            loadLinks();
        } catch (err: any) {
            alert(err.message || 'Failed to toggle link');
        }
    };

    const handleDelete = async (link: SchedulingLink) => {
        if (!confirm(`Delete "${link.name}"? This cannot be undone.`)) return;
        try {
            await schedulingLinksApi.delete(link.id);
            loadLinks();
        } catch (err: any) {
            alert(err.message || 'Failed to delete link');
        }
    };

    const handleCreateOrUpdate = async (data: SchedulingLinkFormData) => {
        try {
            if (editingLink) {
                await schedulingLinksApi.update(editingLink.id, data);
            } else {
                await schedulingLinksApi.create(data);
            }
            setShowCreateModal(false);
            setEditingLink(null);
            loadLinks();
        } catch (err: any) {
            alert(err.message || 'Failed to save');
        }
    };

    const totalLinks = links.length;
    const activeLinks = links.filter(l => l.is_active).length;
    const totalPending = links.reduce((sum, l) => sum + l.pending_count, 0);

    if (isLoading) {
        return (
            <div className="page container">
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    return (
        <div className="page container">
            <div className="dashboard-hero">
                <div className="dashboard-hero-content">
                    <div>
                        <h1 className="dashboard-hero-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <LinkIcon size={28} />
                            Scheduling Links
                        </h1>
                        <p className="dashboard-hero-subtitle">
                            Create shareable links for others to propose meeting times
                        </p>
                    </div>
                    <div className="dashboard-hero-actions">
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="btn btn-primary"
                            style={{
                                background: 'white',
                                color: 'var(--color-navy)'
                            }}
                        >
                            <Plus size={18} />
                            Create Link
                        </button>
                    </div>
                </div>
            </div>

            <div className="dashboard-stats" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: 'var(--color-primary-light)', color: 'var(--color-primary)' }}>
                        <LinkIcon size={22} />
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{totalLinks}</span>
                        <span className="stat-label">Total Links</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#dcfce7', color: '#22c55e' }}>
                        <Check size={22} />
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{activeLinks}</span>
                        <span className="stat-label">Active Links</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{
                        background: totalPending > 0 ? '#fef3c7' : 'var(--color-gray-100)',
                        color: totalPending > 0 ? '#f59e0b' : 'var(--color-gray-500)'
                    }}>
                        <Users size={22} />
                    </div>
                    <div className="stat-content">
                        <span className="stat-value">{totalPending}</span>
                        <span className="stat-label">Pending Proposals</span>
                    </div>
                    {totalPending > 0 && (
                        <span className="stat-badge">Needs review</span>
                    )}
                </div>
            </div>

            <div className="dashboard-events-section">
                <div className="dashboard-events-header">
                    <h2>Your Links</h2>
                </div>

                {links.length === 0 ? (
                    <div className="empty-state-card">
                        <LinkIcon size={56} className="empty-state-icon" />
                        <h3>No scheduling links yet</h3>
                        <p>
                            Create a scheduling link to let others propose meeting times based on your calendar availability.
                        </p>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="btn btn-primary"
                        >
                            <Plus size={18} />
                            Create Your First Link
                        </button>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                        {links.map(link => (
                            <div
                                key={link.id}
                                className="scheduling-link-card"
                                style={{
                                    opacity: link.is_active ? 1 : 0.7,
                                    background: link.is_active
                                        ? 'linear-gradient(135deg, white 0%, var(--color-gray-50) 100%)'
                                        : 'var(--color-gray-100)',
                                    borderColor: link.is_active ? 'var(--color-primary)' : 'var(--color-gray-300)'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', flexWrap: 'wrap' }}>
                                            <h3 style={{ margin: 0, color: 'var(--color-navy)' }}>{link.name}</h3>
                                            <span className={`link-type-badge ${link.booking_type}`}>
                                                {link.booking_type === 'direct' ? <Zap size={10} /> : <ClipboardList size={10} />}
                                                {link.booking_type === 'direct' ? 'Instant' : 'Propose'}
                                            </span>
                                            {!link.is_active && (
                                                <span className="link-status-badge inactive">
                                                    Inactive
                                                </span>
                                            )}
                                            {link.pending_count > 0 && (
                                                <span className="link-status-badge pending">
                                                    {link.pending_count} pending
                                                </span>
                                            )}
                                        </div>

                                        {link.description && (
                                            <p style={{ color: 'var(--color-gray-500)', marginBottom: 'var(--space-3)' }}>
                                                {link.description}
                                            </p>
                                        )}

                                        <div className="link-meta-row">
                                            <span>
                                                <Clock size={14} />
                                                {link.duration_minutes} min
                                            </span>
                                            <span>
                                                <Calendar size={14} />
                                                {link.advance_days_min}-{link.advance_days_max} days out
                                            </span>
                                            <span>
                                                <Users size={14} />
                                                {link.total_proposals} total proposals
                                            </span>
                                        </div>

                                        <div className="link-url-box">
                                            <code>{link.booking_url}</code>
                                            <button
                                                onClick={() => handleCopyLink(link)}
                                                className="btn btn-sm btn-secondary"
                                            >
                                                {copiedId === link.id ? <Check size={14} /> : <Copy size={14} />}
                                                {copiedId === link.id ? 'Copied!' : 'Copy'}
                                            </button>
                                            <button
                                                onClick={() => setQrCodeLink(link)}
                                                className="btn btn-sm btn-secondary"
                                                title="Show QR Code"
                                            >
                                                <QrCode size={14} />
                                            </button>
                                            <a
                                                href={link.booking_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-sm btn-secondary"
                                            >
                                                <ExternalLink size={14} />
                                            </a>
                                        </div>
                                    </div>

                                    <div className="link-actions">
                                        <button
                                            onClick={() => handleToggle(link)}
                                            className="btn btn-sm btn-secondary"
                                            title={link.is_active ? 'Deactivate' : 'Activate'}
                                        >
                                            {link.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setEditingLink(link);
                                                setShowCreateModal(true);
                                            }}
                                            className="btn btn-sm btn-secondary"
                                            title="Edit"
                                        >
                                            <Edit size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(link)}
                                            className="btn btn-sm btn-secondary"
                                            title="Delete"
                                            style={{ color: 'var(--color-error)' }}
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>

                                {link.pending_count > 0 && (
                                    <Link
                                        to={`/proposals?link_id=${link.id}`}
                                        className="link-pending-banner"
                                    >
                                        View {link.pending_count} pending proposal{link.pending_count !== 1 ? 's' : ''}
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showCreateModal && (
                <LinkFormModal
                    link={editingLink}
                    onClose={() => {
                        setShowCreateModal(false);
                        setEditingLink(null);
                    }}
                    onSubmit={handleCreateOrUpdate}
                />
            )}

            {qrCodeLink && (
                <QRCodeModal
                    link={qrCodeLink}
                    onClose={() => setQrCodeLink(null)}
                />
            )}
        </div>
    );
}


function LinkFormModal({
    link,
    onClose,
    onSubmit
}: {
    link: SchedulingLink | null;
    onClose: () => void;
    onSubmit: (data: SchedulingLinkFormData) => Promise<void>;
}) {
    const [name, setName] = useState(link?.name || '');
    const [description, setDescription] = useState(link?.description || '');
    const [bookingType, setBookingType] = useState<'proposal' | 'direct'>(link?.booking_type || 'proposal');
    const [durationMinutes, setDurationMinutes] = useState(link?.duration_minutes || 30);
    const [bufferBefore, setBufferBefore] = useState(link?.buffer_before || 0);
    const [bufferAfter, setBufferAfter] = useState(link?.buffer_after || 0);
    const [advanceDaysMin, setAdvanceDaysMin] = useState(link?.advance_days_min || 1);
    const [advanceDaysMax, setAdvanceDaysMax] = useState(link?.advance_days_max || 28);
    const [maxProposals, setMaxProposals] = useState(link?.max_proposals || 3);
    const [defaultTheme, setDefaultTheme] = useState<'dark' | 'light' | 'system'>(link?.default_theme || 'dark');
    const [timezone, setTimezone] = useState<string>(link?.timezone || detectTimezone());
    const [hubspotEnabled, setHubspotEnabled] = useState(link?.hubspot_enabled || false);
    const [hubspotLeadStatus, setHubspotLeadStatus] = useState<HubSpotLeadStatus | ''>(link?.hubspot_lead_status || '');
    const [workHourStart, setWorkHourStart] = useState(link?.work_hour_start ?? 8);
    const [workHourEnd, setWorkHourEnd] = useState(link?.work_hour_end ?? 17);
    const [workDays, setWorkDays] = useState<number[]>(link?.work_days ?? [1, 2, 3, 4, 5]);
    const [embedHideTitle, setEmbedHideTitle] = useState(link?.embed_hide_title || false);
    const [embedHideLabels, setEmbedHideLabels] = useState(link?.embed_hide_labels || false);
    const [confirmationMessage, setConfirmationMessage] = useState(link?.confirmation_message || '');
    const [confirmationRedirectUrl, setConfirmationRedirectUrl] = useState(link?.confirmation_redirect_url || '');
    const [confirmationRedirectDelay, setConfirmationRedirectDelay] = useState<number | null>(link?.confirmation_redirect_delay ?? null);
    const [confirmationResources, setConfirmationResources] = useState<{ label: string; url: string }[]>(
        link?.confirmation_resources || []
    );
    
    const [allowReschedule, setAllowReschedule] = useState(link?.allow_reschedule ?? true);
    const [allowCancel, setAllowCancel] = useState(link?.allow_cancel ?? true);
    const [minNoticeHours, setMinNoticeHours] = useState(link?.min_notice_hours ?? 24);
    
    const [isSaving, setIsSaving] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showBlocking, setShowBlocking] = useState(false);
    const [showReminders, setShowReminders] = useState(false);
    const [showCustomFields, setShowCustomFields] = useState(false);
    const [showConditionalReminders, setShowConditionalReminders] = useState(false);
    const [showConfirmationPage, setShowConfirmationPage] = useState(false);
    const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);

    const { hubspotConnected } = useAuth();

    const [reminders, setReminders] = useState<number[]>(link?.reminders || []);
    const [reminderEmailBody, setReminderEmailBody] = useState<string>(link?.reminder_email_body || '');
    const [showEmailTemplate, setShowEmailTemplate] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewHtml, setPreviewHtml] = useState<string>('');
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [previewEmailType, setPreviewEmailType] = useState<'reminder' | 'direct_booking' | 'proposal_confirmed' | 'proposal_received' | 'proposal_declined'>('reminder');

    const [logoUrl, setLogoUrl] = useState<string | null>(link?.logo_url || null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(link?.logo_url || null);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);

    const [blockedDates, setBlockedDates] = useState<{ blocked_date: string; reason?: string }[]>(
        link?.blocked_dates?.map(d => ({ blocked_date: d.blocked_date, reason: d.reason || undefined })) || []
    );
    const [newBlockedDate, setNewBlockedDate] = useState('');
    const [newBlockedDateReason, setNewBlockedDateReason] = useState('');

    const [blockedTimes, setBlockedTimes] = useState<{ day_of_week: number | null; start_time: string; end_time: string; reason?: string }[]>(
        link?.blocked_times?.map(t => ({
            day_of_week: t.day_of_week,
            start_time: t.start_time,
            end_time: t.end_time,
            reason: t.reason || undefined
        })) || []
    );
    const [newTimeDay, setNewTimeDay] = useState<number | null>(null);
    const [newTimeStart, setNewTimeStart] = useState('12:00:00');
    const [newTimeEnd, setNewTimeEnd] = useState('13:00:00');
    const [newTimeReason, setNewTimeReason] = useState('');

    type CustomField = {
        field_type: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox';
        field_label: string;
        field_name: string;
        placeholder?: string;
        is_required: boolean;
        display_order: number;
        options?: string[];
        hubspot_property?: string;
    };
    const [customFields, setCustomFields] = useState<CustomField[]>(
        link?.custom_fields?.map((f, idx) => ({
            field_type: f.field_type,
            field_label: f.field_label,
            field_name: f.field_name,
            placeholder: f.placeholder || undefined,
            is_required: f.is_required,
            display_order: f.display_order || idx,
            options: f.options || undefined,
            hubspot_property: f.hubspot_property || undefined
        })) || []
    );
    const [newFieldType, setNewFieldType] = useState<'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox'>('text');
    const [newFieldLabel, setNewFieldLabel] = useState('');
    const [newFieldPlaceholder, setNewFieldPlaceholder] = useState('');
    const [newFieldRequired, setNewFieldRequired] = useState(false);
    const [newFieldOptions, setNewFieldOptions] = useState('');
    const [newFieldHubSpotProperty, setNewFieldHubSpotProperty] = useState('');
    const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);

    const addBlockedDate = () => {
        if (!newBlockedDate) return;
        if (blockedDates.some(d => d.blocked_date === newBlockedDate)) {
            alert('This date is already blocked');
            return;
        }
        setBlockedDates([...blockedDates, { blocked_date: newBlockedDate, reason: newBlockedDateReason || undefined }]);
        setNewBlockedDate('');
        setNewBlockedDateReason('');
    };

    const removeBlockedDate = (index: number) => {
        setBlockedDates(blockedDates.filter((_, i) => i !== index));
    };

    const addBlockedTime = () => {
        if (newTimeStart >= newTimeEnd) {
            alert('End time must be after start time');
            return;
        }
        setBlockedTimes([...blockedTimes, {
            day_of_week: newTimeDay,
            start_time: newTimeStart,
            end_time: newTimeEnd,
            reason: newTimeReason || undefined
        }]);
        setNewTimeReason('');
    };

    const removeBlockedTime = (index: number) => {
        setBlockedTimes(blockedTimes.filter((_, i) => i !== index));
    };

    const generateFieldName = (label: string): string => {
        return label
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');
    };

    const addCustomField = () => {
        if (!newFieldLabel.trim()) {
            alert('Please enter a field label');
            return;
        }

        const fieldName = generateFieldName(newFieldLabel);

        if (customFields.some((f, idx) => f.field_name === fieldName && idx !== editingFieldIndex)) {
            alert('A field with this name already exists. Please use a different label.');
            return;
        }

        let options: string[] | undefined = undefined;
        if ((newFieldType === 'select' || newFieldType === 'checkbox') && newFieldOptions.trim()) {
            options = newFieldOptions.split('\n').map(o => o.trim()).filter(o => o);
            if (options.length === 0) {
                alert('Please provide at least one option for this field type');
                return;
            }
        }

        const newField: CustomField = {
            field_type: newFieldType,
            field_label: newFieldLabel.trim(),
            field_name: fieldName,
            placeholder: newFieldPlaceholder.trim() || undefined,
            is_required: newFieldRequired,
            display_order: editingFieldIndex !== null ? customFields[editingFieldIndex].display_order : customFields.length,
            options,
            hubspot_property: newFieldHubSpotProperty.trim() || undefined
        };

        if (editingFieldIndex !== null) {
            const updatedFields = [...customFields];
            updatedFields[editingFieldIndex] = newField;
            setCustomFields(updatedFields);
            setEditingFieldIndex(null);
        } else {
            setCustomFields([...customFields, newField]);
        }

        setNewFieldLabel('');
        setNewFieldPlaceholder('');
        setNewFieldRequired(false);
        setNewFieldOptions('');
        setNewFieldHubSpotProperty('');
        setNewFieldType('text');
    };

    const editCustomField = (index: number) => {
        const field = customFields[index];
        setNewFieldType(field.field_type);
        setNewFieldLabel(field.field_label);
        setNewFieldPlaceholder(field.placeholder || '');
        setNewFieldRequired(field.is_required);
        setNewFieldOptions(field.options?.join('\n') || '');
        setNewFieldHubSpotProperty(field.hubspot_property || '');
        setEditingFieldIndex(index);
    };

    const cancelEditCustomField = () => {
        setNewFieldLabel('');
        setNewFieldPlaceholder('');
        setNewFieldRequired(false);
        setNewFieldOptions('');
        setNewFieldHubSpotProperty('');
        setNewFieldType('text');
        setEditingFieldIndex(null);
    };

    const removeCustomField = (index: number) => {
        setCustomFields(customFields.filter((_, i) => i !== index));
        if (editingFieldIndex !== null) {
            cancelEditCustomField();
        }
    };

    const moveCustomField = (index: number, direction: 'up' | 'down') => {
        const newFields = [...customFields];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newFields.length) return;

        [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];

        newFields.forEach((field, idx) => {
            field.display_order = idx;
        });

        setCustomFields(newFields);

        if (editingFieldIndex !== null) {
            cancelEditCustomField();
        }
    };

    const toggleReminder = (minutes: number) => {
        if (reminders.includes(minutes)) {
            setReminders(reminders.filter(m => m !== minutes));
        } else {
            setReminders([...reminders, minutes].sort((a, b) => a - b));
        }
    };

    const formatTime = (time: string) => {
        const [h] = time.split(':');
        const hour = parseInt(h);
        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const period = hour < 12 ? 'AM' : 'PM';
        return `${hour12}:00 ${period}`;
    };

    const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Please select a valid image file (PNG, JPG, GIF, or WebP)');
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            alert('Logo file must be less than 2MB');
            return;
        }

        setLogoFile(file);
        const previewUrl = URL.createObjectURL(file);
        setLogoPreview(previewUrl);
    };

    const handleLogoUpload = async () => {
        if (!logoFile || !link) return;

        setIsUploadingLogo(true);
        try {
            const updatedLink = await schedulingLinksApi.uploadLogo(link.id, logoFile);
            setLogoUrl(updatedLink.logo_url || null);
            setLogoPreview(updatedLink.logo_url || null);
            setLogoFile(null);
        } catch (err: any) {
            alert(err.message || 'Failed to upload logo');
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const handleLogoDelete = async () => {
        if (!link || !logoUrl) return;

        if (!confirm('Delete the logo?')) return;

        setIsUploadingLogo(true);
        try {
            await schedulingLinksApi.deleteLogo(link.id);
            setLogoUrl(null);
            setLogoPreview(null);
            setLogoFile(null);
        } catch (err: any) {
            alert(err.message || 'Failed to delete logo');
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const clearLogoSelection = () => {
        setLogoFile(null);
        if (logoUrl) {
            setLogoPreview(logoUrl);
        } else {
            setLogoPreview(null);
        }
    };

    const handlePreviewEmail = async () => {
        setIsLoadingPreview(true);
        try {
            let html: string;

            switch (previewEmailType) {
                case 'reminder':
                    html = await schedulingLinksApi.previewReminderEmail({
                        meeting_name: name.trim() || 'Meeting',
                        duration_minutes: durationMinutes,
                        minutes_before: reminders[0] || 15,
                        theme: defaultTheme === 'system' ? 'dark' : defaultTheme,
                        custom_body: reminderEmailBody.trim() || null,
                        include_teams_link: true,
                    });
                    break;
                case 'direct_booking':
                    html = await schedulingLinksApi.previewDirectBookingEmail({
                        requester_name: 'John Smith',
                        requester_email: 'john.smith@example.com',
                        meeting_name: name.trim() || 'Meeting',
                        duration_minutes: durationMinutes,
                        message: 'Looking forward to our meeting!',
                        include_teams_link: true,
                    });
                    break;
                case 'proposal_confirmed':
                    html = await schedulingLinksApi.previewProposalConfirmedEmail({
                        requester_name: 'John Smith',
                        requester_email: 'john.smith@example.com',
                        meeting_name: name.trim() || 'Meeting',
                        duration_minutes: durationMinutes,
                        include_teams_link: true,
                    });
                    break;
                case 'proposal_received':
                    html = await schedulingLinksApi.previewProposalReceivedEmail({
                        requester_name: 'John Smith',
                        requester_email: 'john.smith@example.com',
                        meeting_name: name.trim() || 'Meeting',
                        duration_minutes: durationMinutes,
                        message: 'Looking forward to discussing the project with you!',
                    });
                    break;
                case 'proposal_declined':
                    html = await schedulingLinksApi.previewProposalDeclinedEmail({
                        requester_name: 'John Smith',
                        meeting_name: name.trim() || 'Meeting',
                        reason: 'Unfortunately, I have a scheduling conflict during these times.',
                    });
                    break;
                default:
                    throw new Error('Invalid email type');
            }

            setPreviewHtml(html);
            setShowPreviewModal(true);
        } catch (err: any) {
            alert(err.message || 'Failed to generate preview');
        } finally {
            setIsLoadingPreview(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            alert('Please enter a name for the link');
            return;
        }

        setIsSaving(true);
        try {
            await onSubmit({
                name: name.trim(),
                description: description.trim() || undefined,
                booking_type: bookingType,
                duration_minutes: durationMinutes,
                buffer_before: bufferBefore,
                buffer_after: bufferAfter,
                advance_days_min: advanceDaysMin,
                advance_days_max: advanceDaysMax,
                max_proposals: maxProposals,
                default_theme: defaultTheme,
                timezone: timezone,
                hubspot_enabled: hubspotEnabled,
                hubspot_lead_status: hubspotEnabled && hubspotLeadStatus ? hubspotLeadStatus : null,
                work_hour_start: workHourStart,
                work_hour_end: workHourEnd,
                work_days: workDays,
                embed_hide_title: embedHideTitle,
                embed_hide_labels: embedHideLabels,
                confirmation_message: confirmationMessage.trim() || null,
                confirmation_redirect_url: confirmationRedirectUrl.trim() || null,
                confirmation_redirect_delay: confirmationRedirectUrl.trim() ? confirmationRedirectDelay : null,
                confirmation_resources: confirmationResources.length > 0 ? confirmationResources : null,
                allow_reschedule: allowReschedule,
                allow_cancel: allowCancel,
                min_notice_hours: minNoticeHours,
                blocked_dates: blockedDates,
                blocked_times: blockedTimes,
                reminders: reminders,
                reminder_email_body: reminderEmailBody.trim() || null,
                custom_fields: customFields.length > 0 ? customFields : undefined
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--space-4)'
        }}>
            <div className="card" style={{ maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                    <h2 style={{ margin: 0 }}>{link ? 'Edit' : 'Create'} Scheduling Link</h2>
                    <button onClick={onClose} className="btn btn-secondary btn-sm">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label className="form-label">Link Name *</label>
                        <input
                            type="text"
                            className="form-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., 30-min Discovery Call"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Description (optional)</label>
                        <textarea
                            className="form-textarea"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Brief description shown to requesters"
                            rows={2}
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <ImageIcon size={14} />
                            Logo (optional)
                        </label>
                        <p className="form-hint" style={{ marginBottom: 'var(--space-2)' }}>
                            Add a logo to brand your booking page. Max 2MB (PNG, JPG, GIF, WebP)
                        </p>

                        {logoPreview ? (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-3)',
                                padding: 'var(--space-3)',
                                background: 'var(--color-gray-50)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-gray-200)'
                            }}>
                                <img
                                    src={logoPreview}
                                    alt="Logo preview"
                                    style={{
                                        width: '64px',
                                        height: '64px',
                                        objectFit: 'contain',
                                        borderRadius: 'var(--radius-sm)',
                                        background: 'white'
                                    }}
                                />
                                <div style={{ flex: 1 }}>
                                    {logoFile ? (
                                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                                            {logoFile.name}
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                                            Current logo
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                    {logoFile && link && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={handleLogoUpload}
                                                disabled={isUploadingLogo}
                                                className="btn btn-sm btn-primary"
                                            >
                                                {isUploadingLogo ? 'Uploading...' : 'Upload'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={clearLogoSelection}
                                                className="btn btn-sm btn-secondary"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    )}
                                    {!logoFile && logoUrl && (
                                        <button
                                            type="button"
                                            onClick={handleLogoDelete}
                                            disabled={isUploadingLogo}
                                            className="btn btn-sm btn-secondary"
                                            style={{ color: 'var(--color-error)' }}
                                        >
                                            <Trash2 size={14} />
                                            Remove
                                        </button>
                                    )}
                                    {!logoFile && (
                                        <label className="btn btn-sm btn-secondary" style={{ cursor: 'pointer' }}>
                                            <Upload size={14} />
                                            Change
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                                onChange={handleLogoSelect}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <label style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                padding: 'var(--space-4)',
                                background: 'var(--color-gray-50)',
                                borderRadius: 'var(--radius-md)',
                                border: '2px dashed var(--color-gray-300)',
                                cursor: 'pointer',
                                transition: 'all var(--transition-fast)'
                            }}>
                                <Upload size={24} style={{ color: 'var(--color-gray-400)' }} />
                                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                                    {link ? 'Click to upload a logo' : 'Save the link first, then add a logo'}
                                </span>
                                {link && (
                                    <input
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                        onChange={handleLogoSelect}
                                        style={{ display: 'none' }}
                                    />
                                )}
                            </label>
                        )}
                    </div>

                    <div className="form-group">
                        <label className="form-label">Booking Mode</label>
                        <div className="form-grid-2" style={{ gap: 'var(--space-2)' }}>
                            <button
                                type="button"
                                onClick={() => setBookingType('proposal')}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-3)',
                                    border: `2px solid ${bookingType === 'proposal' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    background: bookingType === 'proposal' ? 'var(--color-primary-light)' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <ClipboardList size={24} style={{ color: bookingType === 'proposal' ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                                <span style={{ fontWeight: 600, color: bookingType === 'proposal' ? 'var(--color-primary)' : 'var(--color-gray-700)' }}>
                                    Propose Times
                                </span>
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', textAlign: 'center' }}>
                                    Invitees suggest times for you to approve
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setBookingType('direct')}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-3)',
                                    border: `2px solid ${bookingType === 'direct' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    background: bookingType === 'direct' ? 'var(--color-primary-light)' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <Zap size={24} style={{ color: bookingType === 'direct' ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                                <span style={{ fontWeight: 600, color: bookingType === 'direct' ? 'var(--color-primary)' : 'var(--color-gray-700)' }}>
                                    Instant Booking
                                </span>
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', textAlign: 'center' }}>
                                    Invitees book directly with Teams meeting
                                </span>
                            </button>
                        </div>
                    </div>

                    {bookingType === 'direct' && (
                        <div className="form-group" style={{
                            padding: 'var(--space-3)',
                            background: hubspotEnabled ? '#fef3c7' : 'var(--color-gray-50)',
                            border: `1px solid ${hubspotEnabled ? '#fbbf24' : 'var(--color-gray-200)'}`,
                            borderRadius: 'var(--radius-md)'
                        }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: hubspotConnected ? 'pointer' : 'not-allowed',
                                opacity: hubspotConnected ? 1 : 0.6
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M18.7 7.3c-.4-.4-.9-.6-1.5-.6h-1.4V5.5c0-1.4-1.1-2.5-2.5-2.5h-2.5c-1.4 0-2.5 1.1-2.5 2.5v1.2H6.8c-.6 0-1.1.2-1.5.6-.4.4-.6.9-.6 1.5v10.4c0 .6.2 1.1.6 1.5.4.4.9.6 1.5.6h10.4c.6 0 1.1-.2 1.5-.6.4-.4.6-.9.6-1.5V8.8c0-.6-.2-1.1-.6-1.5zM9.9 5.5c0-.5.4-.9.9-.9h2.5c.5 0 .9.4.9.9v1.2H9.9V5.5zm8.4 13.7c0 .2-.1.4-.2.5-.1.1-.3.2-.5.2H6.8c-.2 0-.4-.1-.5-.2-.1-.1-.2-.3-.2-.5V8.8c0-.2.1-.4.2-.5.1-.1.3-.2.5-.2h10.4c.2 0 .4.1.5.2.1.1.2.3.2.5v10.4z" fill="currentColor" />
                                        <circle cx="12" cy="14" r="2" fill="currentColor" />
                                    </svg>
                                    <div>
                                        <span style={{ fontWeight: 600 }}>Sync to HubSpot CRM</span>
                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)', marginTop: '2px' }}>
                                            {hubspotConnected
                                                ? 'Create contacts and log meetings automatically'
                                                : 'Connect HubSpot in settings to enable'
                                            }
                                        </div>
                                    </div>
                                </div>
                                <input
                                    type="checkbox"
                                    className="toggle toggle-primary"
                                    checked={hubspotEnabled}
                                    onChange={(e) => {
                                        const enabled = e.target.checked;
                                        setHubspotEnabled(enabled);
                                        if (!enabled) {
                                            setHubspotLeadStatus('');
                                        }
                                    }}
                                    disabled={!hubspotConnected}
                                />
                            </label>
                            {hubspotEnabled && hubspotConnected && (
                                <div style={{ marginTop: 'var(--space-3)' }}>
                                    <label className="label">
                                        <span className="label-text" style={{ fontWeight: 500 }}>Lead Status</span>
                                    </label>
                                    <select
                                        className="select select-bordered select-sm w-full"
                                        value={hubspotLeadStatus}
                                        onChange={(e) => setHubspotLeadStatus(e.target.value as HubSpotLeadStatus | '')}
                                    >
                                        {HUBSPOT_LEAD_STATUS_OPTIONS.map((option) => (
                                            <option key={option.value || 'none'} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-600)', marginTop: 'var(--space-1)' }}>
                                        Applied when contacts are created or updated in HubSpot
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="form-group">
                        <label className="form-label">Duration</label>
                        <select
                            className="form-select"
                            value={durationMinutes}
                            onChange={(e) => setDurationMinutes(Number(e.target.value))}
                        >
                            {DURATIONS.map(d => (
                                <option key={d.value} value={d.value}>{d.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Default Theme</label>
                        <div className="form-grid-3">
                            <button
                                type="button"
                                onClick={() => setDefaultTheme('dark')}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    padding: 'var(--space-2)',
                                    border: `2px solid ${defaultTheme === 'dark' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    background: defaultTheme === 'dark' ? 'var(--color-primary-light)' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <Moon size={20} style={{ color: defaultTheme === 'dark' ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500, color: defaultTheme === 'dark' ? 'var(--color-primary)' : 'var(--color-gray-600)' }}>
                                    Dark
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setDefaultTheme('light')}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    padding: 'var(--space-2)',
                                    border: `2px solid ${defaultTheme === 'light' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    background: defaultTheme === 'light' ? 'var(--color-primary-light)' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <Sun size={20} style={{ color: defaultTheme === 'light' ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500, color: defaultTheme === 'light' ? 'var(--color-primary)' : 'var(--color-gray-600)' }}>
                                    Light
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setDefaultTheme('system')}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    padding: 'var(--space-2)',
                                    border: `2px solid ${defaultTheme === 'system' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    background: defaultTheme === 'system' ? 'var(--color-primary-light)' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <Monitor size={20} style={{ color: defaultTheme === 'system' ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500, color: defaultTheme === 'system' ? 'var(--color-primary)' : 'var(--color-gray-600)' }}>
                                    System
                                </span>
                            </button>
                        </div>
                        <p className="form-hint">
                            Theme shown to visitors. They can still toggle between themes.
                        </p>
                    </div>

                    <div className="form-group" style={{
                        padding: 'var(--space-3)',
                        background: 'var(--color-gray-50)',
                        border: '1px solid var(--color-gray-200)',
                        borderRadius: 'var(--radius-md)'
                    }}>
                        <div style={{ marginBottom: 'var(--space-3)' }}>
                            <h4 style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 600,
                                color: 'var(--color-navy)',
                                margin: 0,
                                marginBottom: 'var(--space-1)'
                            }}>
                                Embed Display Options
                            </h4>
                            <p style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-gray-600)',
                                margin: 0
                            }}>
                                Control what is shown when this link is embedded on your website
                            </p>
                        </div>

                        <label style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 'var(--space-2)',
                            cursor: 'pointer',
                            marginBottom: 'var(--space-2)'
                        }}>
                            <input
                                type="checkbox"
                                checked={embedHideTitle}
                                onChange={(e) => setEmbedHideTitle(e.target.checked)}
                                style={{
                                    marginTop: '2px',
                                    cursor: 'pointer',
                                    accentColor: 'var(--color-primary)'
                                }}
                            />
                            <div>
                                <div style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    color: 'var(--color-navy)'
                                }}>
                                    Hide "Enter Details" title in embed
                                </div>
                                <div style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-gray-600)',
                                    marginTop: '2px'
                                }}>
                                    Remove the form title for a cleaner embedded experience
                                </div>
                            </div>
                        </label>

                        <label style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 'var(--space-2)',
                            cursor: 'pointer'
                        }}>
                            <input
                                type="checkbox"
                                checked={embedHideLabels}
                                onChange={(e) => setEmbedHideLabels(e.target.checked)}
                                style={{
                                    marginTop: '2px',
                                    cursor: 'pointer',
                                    accentColor: 'var(--color-primary)'
                                }}
                            />
                            <div>
                                <div style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    color: 'var(--color-navy)'
                                }}>
                                    Hide form field labels in embed
                                </div>
                                <div style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-gray-600)',
                                    marginTop: '2px'
                                }}>
                                    Show only placeholders without field labels for a more compact form
                                </div>
                            </div>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Globe size={14} />
                            Your Timezone
                        </label>
                        <select
                            className="form-select"
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value)}
                        >
                            {TIMEZONES.map(tz => (
                                <option key={tz.value} value={tz.value}>
                                    {tz.label}
                                </option>
                            ))}
                        </select>
                        <p className="form-hint">
                            Blocked times will be in this timezone
                        </p>
                    </div>

                    {bookingType === 'proposal' && (
                        <div className="form-group">
                            <label className="form-label">Max time preferences per request</label>
                            <select
                                className="form-select"
                                value={maxProposals}
                                onChange={(e) => setMaxProposals(Number(e.target.value))}
                            >
                                {[1, 2, 3, 4, 5].map(n => (
                                    <option key={n} value={n}>
                                        {n} time{n > 1 ? 's' : ''}
                                    </option>
                                ))}
                            </select>
                            <p className="form-hint">
                                How many time options can requesters propose
                            </p>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <Settings size={14} />
                        {showAdvanced ? 'Hide' : 'Show'} advanced settings
                    </button>

                    {showAdvanced && (
                        <>
                            <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)' }}>
                                <h4 style={{ margin: '0 0 var(--space-3) 0', fontSize: 'var(--font-size-sm)', textTransform: 'uppercase', color: 'var(--color-gray-500)' }}>
                                    Weekly Hours
                                </h4>

                                <div className="form-grid-2" style={{ marginBottom: 'var(--space-3)' }}>
                                    <div className="form-group">
                                        <label className="form-label">Start Time</label>
                                        <select
                                            className="form-select"
                                            value={workHourStart}
                                            onChange={(e) => setWorkHourStart(Number(e.target.value))}
                                        >
                                            {Array.from({ length: 24 }).map((_, i) => (
                                                <option key={`start-${i}`} value={i}>
                                                    {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">End Time</label>
                                        <select
                                            className="form-select"
                                            value={workHourEnd}
                                            onChange={(e) => setWorkHourEnd(Number(e.target.value))}
                                        >
                                            {Array.from({ length: 24 }).map((_, i) => (
                                                <option key={`end-${i}`} value={i}>
                                                    {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Work Days</label>
                                    <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                                        {DAYS_OF_WEEK.slice(1).map((day) => (
                                            <label key={day.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={workDays.includes(day.value as number)}
                                                    onChange={(e) => {
                                                        const val = day.value as number;
                                                        if (e.target.checked) {
                                                            setWorkDays([...workDays, val].sort());
                                                        } else {
                                                            setWorkDays(workDays.filter(d => d !== val));
                                                        }
                                                    }}
                                                />
                                                {day.label.slice(0, 3)}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="form-grid-2">
                                <div className="form-group">
                                    <label className="form-label">Availability starts</label>
                                    <select
                                        className="form-select"
                                        value={advanceDaysMin}
                                        onChange={(e) => setAdvanceDaysMin(Number(e.target.value))}
                                    >
                                        <option value={0.25}>In 6 hours</option>
                                        <option value={0.5}>In 12 hours</option>
                                        <option value={0}>Today</option>
                                        <option value={1}>Tomorrow</option>
                                        <option value={2}>In 2 days</option>
                                        <option value={3}>In 3 days</option>
                                        <option value={7}>In 1 week</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Availability ends</label>
                                    <select
                                        className="form-select"
                                        value={advanceDaysMax}
                                        onChange={(e) => setAdvanceDaysMax(Number(e.target.value))}
                                    >
                                        <option value={7}>1 week out</option>
                                        <option value={14}>2 weeks out</option>
                                        <option value={21}>3 weeks out</option>
                                        <option value={28}>4 weeks out</option>
                                        <option value={60}>2 months out</option>
                                        <option value={90}>3 months out</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-grid-2">
                                <div className="form-group">
                                    <label className="form-label">Buffer before meeting</label>
                                    <select
                                        className="form-select"
                                        value={bufferBefore}
                                        onChange={(e) => setBufferBefore(Number(e.target.value))}
                                    >
                                        <option value={0}>None</option>
                                        <option value={5}>5 minutes</option>
                                        <option value={10}>10 minutes</option>
                                        <option value={15}>15 minutes</option>
                                        <option value={30}>30 minutes</option>
                                        <option value={60}>1 hour</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Buffer after meeting</label>
                                    <select
                                        className="form-select"
                                        value={bufferAfter}
                                        onChange={(e) => setBufferAfter(Number(e.target.value))}
                                    >
                                        <option value={0}>None</option>
                                        <option value={5}>5 minutes</option>
                                        <option value={10}>10 minutes</option>
                                        <option value={15}>15 minutes</option>
                                        <option value={30}>30 minutes</option>
                                        <option value={60}>1 hour</option>
                                    </select>
                                </div>
                            </div>
                        </>
                    )}

                    <button
                        type="button"
                        onClick={() => setShowConfirmationPage(!showConfirmationPage)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <Check size={14} />
                        {showConfirmationPage ? 'Hide' : 'Show'} confirmation page settings
                        {(confirmationMessage || confirmationRedirectUrl || confirmationResources.length > 0) && (
                            <span style={{
                                background: 'var(--color-primary)',
                                color: 'white',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                fontSize: '11px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '4px'
                            }}>
                                <Check size={10} />
                            </span>
                        )}
                    </button>

                    {showConfirmationPage && (
                        <div style={{
                            background: 'var(--color-gray-50)',
                            padding: 'var(--space-4)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--space-2)' }}>
                                <Check size={16} />
                                Confirmation Page
                            </h4>
                            <p className="form-hint" style={{ marginBottom: 'var(--space-3)' }}>
                                Customize what users see after booking a meeting.
                            </p>

                            <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                                <label className="form-label">Custom Thank You Message</label>
                                <textarea
                                    className="form-input"
                                    value={confirmationMessage}
                                    onChange={(e) => setConfirmationMessage(e.target.value)}
                                    placeholder="Leave blank for default message. Supports: {{name}}, {{email}}, {{meeting_name}}, {{host_name}}"
                                    rows={3}
                                    style={{ resize: 'vertical' }}
                                />
                                <p className="form-hint">
                                    Shown after booking confirmation. Leave empty to use the default message.
                                </p>
                            </div>

                            <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                                <label className="form-label">Redirect After Booking</label>
                                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                    <input
                                        type="url"
                                        className="form-input"
                                        value={confirmationRedirectUrl}
                                        onChange={(e) => setConfirmationRedirectUrl(e.target.value)}
                                        placeholder="https://example.com/thank-you"
                                        style={{ flex: 1 }}
                                    />
                                    {confirmationRedirectUrl && (
                                        <select
                                            className="form-select"
                                            value={confirmationRedirectDelay ?? ''}
                                            onChange={(e) => setConfirmationRedirectDelay(e.target.value === '' ? null : Number(e.target.value))}
                                            style={{ width: '140px' }}
                                        >
                                            <option value="">No auto-redirect</option>
                                            <option value="0">Immediate</option>
                                            <option value="3">After 3 seconds</option>
                                            <option value="5">After 5 seconds</option>
                                            <option value="10">After 10 seconds</option>
                                        </select>
                                    )}
                                </div>
                                <p className="form-hint">
                                    Optionally redirect users to another page after booking.
                                </p>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Additional Resources</label>
                                <p className="form-hint" style={{ marginBottom: 'var(--space-2)' }}>
                                    Add helpful links to display on the confirmation page (e.g., preparation guides, documents).
                                </p>

                                {confirmationResources.map((resource, index) => (
                                    <div key={index} style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={resource.label}
                                            onChange={(e) => {
                                                const updated = [...confirmationResources];
                                                updated[index] = { ...updated[index], label: e.target.value };
                                                setConfirmationResources(updated);
                                            }}
                                            placeholder="Link text"
                                            style={{ flex: 1 }}
                                        />
                                        <input
                                            type="url"
                                            className="form-input"
                                            value={resource.url}
                                            onChange={(e) => {
                                                const updated = [...confirmationResources];
                                                updated[index] = { ...updated[index], url: e.target.value };
                                                setConfirmationResources(updated);
                                            }}
                                            placeholder="https://..."
                                            style={{ flex: 2 }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setConfirmationResources(confirmationResources.filter((_, i) => i !== index));
                                            }}
                                            className="btn btn-secondary btn-sm"
                                            style={{ flexShrink: 0 }}
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    onClick={() => setConfirmationResources([...confirmationResources, { label: '', url: '' }])}
                                    className="btn btn-secondary btn-sm"
                                    style={{ marginTop: 'var(--space-2)' }}
                                >
                                    <Plus size={14} style={{ marginRight: '4px' }} />
                                    Add Resource Link
                                </button>
                            </div>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => setShowCancellationPolicy(!showCancellationPolicy)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <CalendarX size={14} />
                        {showCancellationPolicy ? 'Hide' : 'Show'} cancellation policy
                        {(!allowReschedule || !allowCancel || minNoticeHours !== 24) && (
                            <span style={{
                                background: 'var(--color-primary)',
                                color: 'white',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                fontSize: '11px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '4px'
                            }}>
                                <Check size={10} />
                            </span>
                        )}
                    </button>

                    {showCancellationPolicy && (
                        <div style={{
                            background: 'var(--color-gray-50)',
                            padding: 'var(--space-4)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--space-2)' }}>
                                <CalendarX size={16} />
                                Cancellation Policy
                            </h4>
                            <p className="form-hint" style={{ marginBottom: 'var(--space-3)' }}>
                                Control whether bookers can reschedule or cancel their own meetings.
                            </p>

                            <div style={{ marginBottom: 'var(--space-3)' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    cursor: 'pointer'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={allowReschedule}
                                        onChange={(e) => setAllowReschedule(e.target.checked)}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <span style={{ fontWeight: 500 }}>Allow bookers to reschedule</span>
                                </label>
                                <p className="form-hint" style={{ marginLeft: '26px' }}>
                                    Bookers will see a "Reschedule" option in their confirmation email.
                                </p>
                            </div>

                            <div style={{ marginBottom: 'var(--space-3)' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    cursor: 'pointer'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={allowCancel}
                                        onChange={(e) => setAllowCancel(e.target.checked)}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <span style={{ fontWeight: 500 }}>Allow bookers to cancel</span>
                                </label>
                                <p className="form-hint" style={{ marginLeft: '26px' }}>
                                    Bookers will see a "Cancel" option in their confirmation email.
                                </p>
                            </div>

                            {(allowReschedule || allowCancel) && (
                                <div className="form-group" style={{ marginTop: 'var(--space-4)' }}>
                                    <label className="form-label">Minimum Notice Required</label>
                                    <select
                                        className="form-input"
                                        value={minNoticeHours}
                                        onChange={(e) => setMinNoticeHours(Number(e.target.value))}
                                        style={{ maxWidth: '300px' }}
                                    >
                                        <option value={0}>No minimum (anytime before meeting)</option>
                                        <option value={1}>1 hour before</option>
                                        <option value={2}>2 hours before</option>
                                        <option value={4}>4 hours before</option>
                                        <option value={12}>12 hours before</option>
                                        <option value={24}>24 hours (1 day) before</option>
                                        <option value={48}>48 hours (2 days) before</option>
                                        <option value={72}>72 hours (3 days) before</option>
                                        <option value={168}>1 week before</option>
                                    </select>
                                    <p className="form-hint">
                                        Bookers won't be able to reschedule or cancel within this time before the meeting.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => setShowReminders(!showReminders)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <Bell size={14} />
                        {showReminders ? 'Hide' : 'Show'} booking reminders
                        {reminders.length > 0 && (
                            <span style={{
                                background: 'var(--color-primary)',
                                color: 'white',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                fontSize: '11px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '4px'
                            }}>
                                {reminders.length}
                            </span>
                        )}
                    </button>

                    {showReminders && (
                        <div style={{
                            background: 'var(--color-gray-50)',
                            padding: 'var(--space-4)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--space-2)' }}>
                                <Bell size={16} />
                                Email Reminders
                            </h4>
                            <p className="form-hint" style={{ marginBottom: 'var(--space-3)' }}>
                                Send email reminders to the person who booked before their meeting. Select multiple to send at different times.
                            </p>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                {REMINDER_OPTIONS.map(option => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => toggleReminder(option.value)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: 'var(--space-2) var(--space-3)',
                                            border: `2px solid ${reminders.includes(option.value) ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                            borderRadius: 'var(--radius-full)',
                                            background: reminders.includes(option.value) ? 'var(--color-primary-light)' : 'white',
                                            color: reminders.includes(option.value) ? 'var(--color-primary)' : 'var(--color-gray-600)',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: reminders.includes(option.value) ? 600 : 400,
                                            transition: 'all var(--transition-fast)'
                                        }}
                                    >
                                        {reminders.includes(option.value) && <Check size={14} />}
                                        {option.label}
                                    </button>
                                ))}
                            </div>

                            {reminders.length === 0 && (
                                <p style={{
                                    marginTop: 'var(--space-3)',
                                    color: 'var(--color-gray-500)',
                                    fontSize: 'var(--font-size-sm)',
                                    fontStyle: 'italic'
                                }}>
                                    No reminders configured. Click options above to enable email reminders.
                                </p>
                            )}

                            {reminders.length > 0 && (
                                <div style={{ marginTop: 'var(--space-4)', borderTop: '1px solid var(--color-gray-200)', paddingTop: 'var(--space-4)' }}>
                                    <div style={{ display: 'flex', gap: 'var(--space-4)', marginBottom: showEmailTemplate ? 'var(--space-3)' : 0 }}>
                                        <button
                                            type="button"
                                            onClick={() => setShowEmailTemplate(!showEmailTemplate)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: 'var(--color-primary)',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                fontSize: 'var(--font-size-sm)',
                                                padding: 0
                                            }}
                                        >
                                            <Edit size={14} />
                                            {showEmailTemplate ? 'Hide' : 'Customize'} email template
                                        </button>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <select
                                                value={previewEmailType}
                                                onChange={(e) => setPreviewEmailType(e.target.value as any)}
                                                style={{
                                                    padding: '4px 8px',
                                                    fontSize: 'var(--font-size-sm)',
                                                    border: '1px solid var(--color-gray-300)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    backgroundColor: 'white',
                                                    color: 'var(--color-gray-700)',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <option value="reminder">Reminder Email</option>
                                                <option value="direct_booking">Direct Booking</option>
                                                <option value="proposal_confirmed">Proposal Confirmed</option>
                                                <option value="proposal_received">Proposal Received</option>
                                                <option value="proposal_declined">Proposal Declined</option>
                                            </select>
                                            <button
                                                type="button"
                                                onClick={handlePreviewEmail}
                                                disabled={isLoadingPreview}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: 'var(--color-gray-600)',
                                                    cursor: isLoadingPreview ? 'wait' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    fontSize: 'var(--font-size-sm)',
                                                    padding: 0,
                                                    opacity: isLoadingPreview ? 0.6 : 1
                                                }}
                                            >
                                                <Eye size={14} />
                                                {isLoadingPreview ? 'Loading...' : 'Preview'}
                                            </button>
                                        </div>
                                    </div>

                                    {showEmailTemplate && (
                                        <div>
                                            <p className="form-hint" style={{ marginBottom: 'var(--space-2)' }}>
                                                Customize the reminder email body text. Leave empty to use the default template.
                                                The email will automatically include the meeting date/time and join button.
                                            </p>
                                            <textarea
                                                className="form-textarea"
                                                value={reminderEmailBody}
                                                onChange={(e) => setReminderEmailBody(e.target.value)}
                                                placeholder="Hello,

This is your reminder to join the {{meeting_name}} in {{reminder_time}}.

The link to join the Microsoft Teams screenshare meeting is below, in the calendar invite, as well as a phone number you can dial in to if you don't have a computer microphone."
                                                rows={6}
                                                style={{ fontFamily: 'inherit', fontSize: 'var(--font-size-sm)' }}
                                            />

                                            <details style={{ marginTop: 'var(--space-2)' }}>
                                                <summary style={{
                                                    cursor: 'pointer',
                                                    fontSize: 'var(--font-size-sm)',
                                                    color: 'var(--color-gray-600)',
                                                    userSelect: 'none'
                                                }}>
                                                    Available placeholders
                                                </summary>
                                                <div style={{
                                                    marginTop: 'var(--space-2)',
                                                    padding: 'var(--space-3)',
                                                    background: 'white',
                                                    borderRadius: 'var(--radius-sm)',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}>
                                                    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{recipient_name}}'}</code> — Person receiving the reminder</div>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{host_name}}'}</code> — Your name (meeting host)</div>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{meeting_name}}'}</code> — Name of this scheduling link</div>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{meeting_time}}'}</code> — Full date and time</div>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{meeting_date}}'}</code> — Just the date</div>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{duration}}'}</code> — Meeting duration</div>
                                                        <div><code style={{ background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '3px' }}>{'{{reminder_time}}'}</code> — Time until meeting (e.g., "15 minutes")</div>
                                                    </div>
                                                </div>
                                            </details>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {link && (
                        <>
                            <button
                                type="button"
                                onClick={() => setShowConditionalReminders(!showConditionalReminders)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--color-primary)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    marginBottom: 'var(--space-4)',
                                    fontSize: 'var(--font-size-sm)'
                                }}
                            >
                                <Zap size={14} />
                                {showConditionalReminders ? 'Hide' : 'Show'} attendance follow-ups
                            </button>

                            {showConditionalReminders && (
                                <div style={{
                                    background: 'var(--color-gray-50)',
                                    padding: 'var(--space-4)',
                                    borderRadius: 'var(--radius-md)',
                                    marginBottom: 'var(--space-4)'
                                }}>
                                    <ConditionalReminderSettings
                                        linkId={link.id}
                                        linkName={link.name}
                                        onSave={() => {
                                        }}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    <button
                        type="button"
                        onClick={() => setShowCustomFields(!showCustomFields)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <ClipboardList size={14} />
                        {showCustomFields ? 'Hide' : 'Show'} custom form fields
                        {customFields.length > 0 && (
                            <span style={{
                                background: 'var(--color-primary)',
                                color: 'white',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                fontSize: '11px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '4px'
                            }}>
                                {customFields.length}
                            </span>
                        )}
                    </button>

                    {showCustomFields && (
                        <div style={{
                            background: 'var(--color-gray-50)',
                            padding: 'var(--space-4)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--space-2)' }}>
                                <ClipboardList size={16} />
                                Custom Form Fields
                            </h4>
                            <p className="form-hint" style={{ marginBottom: 'var(--space-3)' }}>
                                Add custom fields to collect additional information from requesters (e.g., phone number, company name, how they heard about you)
                            </p>

                            {customFields.length > 0 && (
                                <div style={{ marginBottom: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                    {customFields.map((field, i) => {
                                        const FieldIcon = FIELD_TYPES.find(t => t.value === field.field_type)?.icon || FileText;
                                        return (
                                            <div
                                                key={i}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    background: editingFieldIndex === i ? 'var(--color-primary-light)' : 'white',
                                                    border: editingFieldIndex === i ? '2px solid var(--color-primary)' : '1px solid var(--color-gray-200)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    padding: 'var(--space-2) var(--space-3)',
                                                    fontSize: 'var(--font-size-sm)',
                                                    transition: 'all var(--transition-fast)'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flex: 1 }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => moveCustomField(i, 'up')}
                                                            disabled={i === 0}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: i === 0 ? 'not-allowed' : 'pointer',
                                                                padding: 0,
                                                                opacity: i === 0 ? 0.3 : 1,
                                                                color: 'var(--color-gray-400)'
                                                            }}
                                                        >
                                                            <ChevronLeft size={12} style={{ transform: 'rotate(90deg)' }} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => moveCustomField(i, 'down')}
                                                            disabled={i === customFields.length - 1}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: i === customFields.length - 1 ? 'not-allowed' : 'pointer',
                                                                padding: 0,
                                                                opacity: i === customFields.length - 1 ? 0.3 : 1,
                                                                color: 'var(--color-gray-400)'
                                                            }}
                                                        >
                                                            <ChevronLeft size={12} style={{ transform: 'rotate(-90deg)' }} />
                                                        </button>
                                                    </div>
                                                    <FieldIcon size={16} style={{ color: 'var(--color-gray-500)' }} />
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 500 }}>
                                                            {field.field_label}
                                                            {field.is_required && <span style={{ color: 'var(--color-error)', marginLeft: '2px' }}>*</span>}
                                                        </div>
                                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>
                                                            {FIELD_TYPES.find(t => t.value === field.field_type)?.label}
                                                            {field.placeholder && ` • "${field.placeholder}"`}
                                                            {field.hubspot_property && ` • HubSpot: ${field.hubspot_property}`}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => editCustomField(i)}
                                                        disabled={editingFieldIndex !== null && editingFieldIndex !== i}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: editingFieldIndex !== null && editingFieldIndex !== i ? 'not-allowed' : 'pointer',
                                                            padding: '4px',
                                                            color: editingFieldIndex === i ? 'var(--color-primary)' : (editingFieldIndex !== null ? 'var(--color-gray-300)' : 'var(--color-primary)'),
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            opacity: editingFieldIndex !== null && editingFieldIndex !== i ? 0.5 : 1
                                                        }}
                                                        title={editingFieldIndex === i ? 'Currently editing' : 'Edit field'}
                                                    >
                                                        <Edit size={14} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeCustomField(i)}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            padding: '4px',
                                                            color: 'var(--color-gray-400)'
                                                        }}
                                                        title="Remove field"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {editingFieldIndex !== null && (
                                <div style={{
                                    marginBottom: 'var(--space-3)',
                                    padding: 'var(--space-2) var(--space-3)',
                                    background: 'var(--color-primary-light)',
                                    border: '1px solid var(--color-primary)',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-primary)',
                                    fontWeight: 500
                                }}>
                                    <Edit size={14} style={{ display: 'inline', marginRight: '6px' }} />
                                    Editing: {customFields[editingFieldIndex].field_label}
                                </div>
                            )}

                            <div className="form-grid-2">
                                <div>
                                    <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>Field Label *</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={newFieldLabel}
                                        onChange={(e) => setNewFieldLabel(e.target.value)}
                                        placeholder="e.g., Phone Number, Company Name"
                                    />
                                </div>
                                <div>
                                    <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>Field Type *</label>
                                    <select
                                        className="form-select"
                                        value={newFieldType}
                                        onChange={(e) => setNewFieldType(e.target.value as any)}
                                    >
                                        {FIELD_TYPES.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-grid-2" style={{ marginTop: 'var(--space-3)' }}>
                                <div>
                                    <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>Placeholder Text</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={newFieldPlaceholder}
                                        onChange={(e) => setNewFieldPlaceholder(e.target.value)}
                                        placeholder="Optional placeholder"
                                    />
                                </div>
                                <div>
                                    <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>HubSpot Property</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={newFieldHubSpotProperty}
                                        onChange={(e) => setNewFieldHubSpotProperty(e.target.value)}
                                        placeholder="e.g., phone, company"
                                        disabled={!hubspotEnabled}
                                    />
                                </div>
                            </div>

                            {(newFieldType === 'select' || newFieldType === 'checkbox') && (
                                <div style={{ marginTop: 'var(--space-3)' }}>
                                    <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>
                                        Options (one per line) *
                                    </label>
                                    <textarea
                                        className="form-textarea"
                                        value={newFieldOptions}
                                        onChange={(e) => setNewFieldOptions(e.target.value)}
                                        placeholder={'SEMA 2025\nEmail\nDix Performance Show\nSearch Engine\nReferral'}
                                        rows={4}
                                    />
                                </div>
                            )}

                            <div style={{ marginTop: 'var(--space-3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={newFieldRequired}
                                        onChange={(e) => setNewFieldRequired(e.target.checked)}
                                    />
                                    <span>Required field</span>
                                </label>
                                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                    {editingFieldIndex !== null && (
                                        <button
                                            type="button"
                                            onClick={cancelEditCustomField}
                                            className="btn btn-outline btn-sm"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={addCustomField}
                                        className="btn btn-secondary btn-sm"
                                        disabled={!newFieldLabel.trim()}
                                    >
                                        {editingFieldIndex !== null ? (
                                            <>
                                                <Check size={16} />
                                                Update Field
                                            </>
                                        ) : (
                                            <>
                                                <Plus size={16} />
                                                Add Field
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => setShowBlocking(!showBlocking)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <Ban size={14} />
                        {showBlocking ? 'Hide' : 'Show'} blocking rules
                        {(blockedDates.length > 0 || blockedTimes.length > 0) && (
                            <span style={{
                                background: 'var(--color-primary)',
                                color: 'white',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                fontSize: '11px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '4px'
                            }}>
                                {blockedDates.length + blockedTimes.length}
                            </span>
                        )}
                    </button>

                    {showBlocking && (
                        <div style={{
                            background: 'var(--color-gray-50)',
                            padding: 'var(--space-4)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <div style={{ marginBottom: 'var(--space-4)' }}>
                                <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--space-2)' }}>
                                    <CalendarX size={16} />
                                    Blocked Dates
                                </h4>
                                <p className="form-hint" style={{ marginBottom: 'var(--space-3)' }}>
                                    Block specific dates (e.g., vacation, holidays)
                                </p>

                                {blockedDates.length > 0 && (
                                    <div style={{ marginBottom: 'var(--space-3)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                        {blockedDates.map((d, i) => (
                                            <span
                                                key={i}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    background: 'white',
                                                    border: '1px solid var(--color-gray-200)',
                                                    borderRadius: 'var(--radius-full)',
                                                    padding: '4px 8px 4px 12px',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}
                                            >
                                                {d.blocked_date}
                                                {d.reason && <span style={{ color: 'var(--color-gray-500)' }}>({d.reason})</span>}
                                                <button
                                                    type="button"
                                                    onClick={() => removeBlockedDate(i)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-gray-400)' }}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
                                    <div style={{ flex: 1 }}>
                                        <input
                                            type="date"
                                            className="form-input"
                                            value={newBlockedDate}
                                            onChange={(e) => setNewBlockedDate(e.target.value)}
                                            min={localTodayStr()}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={newBlockedDateReason}
                                            onChange={(e) => setNewBlockedDateReason(e.target.value)}
                                            placeholder="Reason (optional)"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addBlockedDate}
                                        className="btn btn-secondary btn-sm"
                                        disabled={!newBlockedDate}
                                    >
                                        <Plus size={16} />
                                    </button>
                                </div>
                            </div>

                            <div>
                                <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--space-2)' }}>
                                    <Clock size={16} />
                                    Blocked Time Periods
                                </h4>
                                <p className="form-hint" style={{ marginBottom: 'var(--space-3)' }}>
                                    Block recurring times (e.g., lunch hour, focus time)
                                </p>

                                {blockedTimes.length > 0 && (
                                    <div style={{ marginBottom: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                        {blockedTimes.map((t, i) => (
                                            <div
                                                key={i}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    background: 'white',
                                                    border: '1px solid var(--color-gray-200)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    padding: 'var(--space-2) var(--space-3)',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}
                                            >
                                                <span>
                                                    <strong>{t.day_of_week === null ? 'Every day' : DAYS_OF_WEEK.find(d => d.value === t.day_of_week)?.label}</strong>
                                                    {' '}from {formatTime(t.start_time)} to {formatTime(t.end_time)}
                                                    {t.reason && <span style={{ color: 'var(--color-gray-500)' }}> ({t.reason})</span>}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeBlockedTime(i)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-gray-400)' }}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="form-grid-5">
                                    <div>
                                        <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>Day</label>
                                        <select
                                            className="form-select"
                                            value={newTimeDay === null ? '' : newTimeDay}
                                            onChange={(e) => setNewTimeDay(e.target.value === '' ? null : Number(e.target.value))}
                                        >
                                            {DAYS_OF_WEEK.map(d => (
                                                <option key={d.label} value={d.value === null ? '' : d.value}>{d.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>From</label>
                                        <select
                                            className="form-select"
                                            value={newTimeStart}
                                            onChange={(e) => setNewTimeStart(e.target.value)}
                                        >
                                            {TIMES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>To</label>
                                        <select
                                            className="form-select"
                                            value={newTimeEnd}
                                            onChange={(e) => setNewTimeEnd(e.target.value)}
                                        >
                                            {TIMES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>Reason</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={newTimeReason}
                                            onChange={(e) => setNewTimeReason(e.target.value)}
                                            placeholder="Optional"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addBlockedTime}
                                        className="btn btn-secondary btn-sm"
                                    >
                                        <Plus size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-secondary"
                            style={{ flex: 1 }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="btn btn-primary"
                            style={{ flex: 1 }}
                        >
                            {isSaving ? 'Saving...' : (link ? 'Update' : 'Create')} Link
                        </button>
                    </div>
                </form>
            </div>

            {showPreviewModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1100,
                        padding: 'var(--space-4)'
                    }}
                    onClick={() => setShowPreviewModal(false)}
                >
                    <div
                        style={{
                            background: 'white',
                            borderRadius: 'var(--radius-lg)',
                            maxWidth: '650px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: 'var(--space-4)',
                            borderBottom: '1px solid var(--color-gray-200)'
                        }}>
                            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Eye size={20} />
                                Email Preview
                            </h3>
                            <button
                                onClick={() => setShowPreviewModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    display: 'flex',
                                    color: 'var(--color-gray-500)'
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div style={{
                            flex: 1,
                            overflow: 'auto',
                            background: '#f0f0f0'
                        }}>
                            <iframe
                                srcDoc={previewHtml}
                                style={{
                                    width: '100%',
                                    height: '600px',
                                    border: 'none'
                                }}
                                title="Email Preview"
                            />
                        </div>
                        <div style={{
                            padding: 'var(--space-3)',
                            borderTop: '1px solid var(--color-gray-200)',
                            textAlign: 'center'
                        }}>
                            <p style={{
                                margin: 0,
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-gray-500)'
                            }}>
                                This is a preview with sample data. Actual emails will use real booking information.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function QRCodeModal({
    link,
    onClose
}: {
    link: SchedulingLink;
    onClose: () => void;
}) {
    const qrCodeRef = useRef<HTMLDivElement>(null);
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownload = async () => {
        if (!qrCodeRef.current) return;

        setIsDownloading(true);
        try {
            const svgElement = qrCodeRef.current.querySelector('svg');
            if (!svgElement) {
                alert('Failed to generate QR code image');
                setIsDownloading(false);
                return;
            }

            const clonedSvg = svgElement.cloneNode(true) as SVGElement;
            
            clonedSvg.setAttribute('width', '512');
            clonedSvg.setAttribute('height', '512');
            
            const svgData = new XMLSerializer().serializeToString(clonedSvg);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const svgUrl = URL.createObjectURL(svgBlob);

            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = 512;
                canvas.height = 512;
                const ctx = canvas.getContext('2d');
                
                if (ctx) {
                    ctx.fillStyle = '#FFFFFF';
                    ctx.fillRect(0, 0, 512, 512);
                    
                    ctx.drawImage(img, 0, 0, 512, 512);
                    
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            const filename = link.name
                                ? `${link.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-qr-code.png`
                                : `booking-link-${link.slug}-qr-code.png`;
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                        }
                        URL.revokeObjectURL(svgUrl);
                        setIsDownloading(false);
                    }, 'image/png');
                } else {
                    URL.revokeObjectURL(svgUrl);
                    setIsDownloading(false);
                }
            };
            img.onerror = () => {
                URL.revokeObjectURL(svgUrl);
                setIsDownloading(false);
                alert('Failed to generate QR code image');
            };
            img.src = svgUrl;
        } catch (error) {
            console.error('Failed to download QR code:', error);
            alert('Failed to download QR code');
            setIsDownloading(false);
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1100,
                padding: 'var(--space-4)'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: 'white',
                    borderRadius: 'var(--radius-lg)',
                    maxWidth: '500px',
                    width: '100%',
                    padding: 'var(--space-5)',
                    boxShadow: 'var(--shadow-xl)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 'var(--space-4)'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    paddingBottom: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-gray-200)'
                }}>
                    <h3 style={{ 
                        margin: 0, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 'var(--space-2)',
                        color: 'var(--color-navy)'
                    }}>
                        <QrCode size={20} />
                        QR Code
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            color: 'var(--color-gray-500)',
                            borderRadius: 'var(--radius-sm)'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.background = 'var(--color-gray-100)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = 'none';
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                <div
                    ref={qrCodeRef}
                    style={{
                        padding: 'var(--space-4)',
                        background: 'white',
                        borderRadius: 'var(--radius-md)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <QRCodeSVG
                        value={link.booking_url}
                        size={256}
                        level="M"
                        fgColor="#0A2F5E"
                        bgColor="#FFFFFF"
                        style={{ width: '100%', height: '100%' }}
                    />
                </div>

                <div style={{
                    width: '100%',
                    textAlign: 'center',
                    padding: 'var(--space-3)',
                    background: 'var(--color-gray-50)',
                    borderRadius: 'var(--radius-md)',
                    wordBreak: 'break-all'
                }}>
                    <code style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-gray-700)'
                    }}>
                        {link.booking_url}
                    </code>
                </div>

                <button
                    onClick={handleDownload}
                    disabled={isDownloading}
                    className="btn btn-primary"
                    style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--space-2)'
                    }}
                >
                    <Download size={18} />
                    {isDownloading ? 'Generating...' : 'Download QR Code'}
                </button>
            </div>
        </div>
    );
}
